import * as XLSX from 'xlsx';
import React from 'react';
import { ParseResult, JournalEntry, TableType, TABLE_TYPES, ACCOUNTS, Account } from './parser/types';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  Button,
  Divider,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Autocomplete,
  TextField,
} from "@mui/material";
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { ParseSheetConfig } from './parser/config';
import { getBalance } from './parser/utils';
import { formatCurrency, formatDate } from './utils';
import { RunParseResult } from './run_parse';

interface ResultViewProps {
  runParseResult: RunParseResult;
}

function exportAsExcel(result: RunParseResult) {
  // Use unix time now as "random" starting journal id.
  // Journal ids need to preserve the order of transactions.
  const now = Date.now();

  const rows: Array<Record<string, any>> = [];
  for (const t of TABLE_TYPES) {
    // Append the same journal id to every line in the same group.
    const tableRows = (result.groupsPerTable.get(t) ?? []).map((g, groupIndex) => {
      const journalId = `${t}-${now}-${String(groupIndex).padStart(6, '0')}`;
      return g.map((entry, i) => ({
        JournalID: journalId,
        Line: i+1,
        Date: entry.date,
        Description: entry.description,
        Account: entry.account,
        DebitAmount: entry.debitAmount,
        CreditAmount: entry.creditAmount,
        Property: result.property,
        BankTrxID: null,
        Tenant: entry.tenant,
        Note: entry.note,
      }));
    });
    rows.push(...tableRows);

  }

  const wb = XLSX.utils.book_new();
  const ws = XLSX.utils.json_to_sheet(
    rows.flat(),
    {
      header: [
        "JournalID",
        "Line",
        "Date",
        "Description",
        "Account",
        "DebitAmount",
        "CreditAmount",
        "Property",
        "BankTrxID",
        "Tenant",
        "Note",
      ],
    },
  );
  XLSX.utils.book_append_sheet(wb, ws, "Sheet1");

  const formattedDate = formatDate(result.reportDate).replaceAll('/', '');
  XLSX.writeFile(wb, `${formattedDate}_${result.property}.xlsx`);
}

const journalEntryHeaders: (keyof JournalEntry)[] = [
  'date',
  'description',
  'account',
  'property',
  'tenant',
  'note',
  'creditAmount',
  'debitAmount',
];

export function ResultView({ runParseResult: initialRunParseResult }: ResultViewProps) {
  const [runParseResult, setRunParseResult] = React.useState<RunParseResult>(initialRunParseResult);

  const handleAccountChange = (table: TableType, groupIndex: number, entryIndex: number, newAccount: Account) => {
    const newGroupsPerTable = new Map(runParseResult.groupsPerTable);
    const newGroups = [...(newGroupsPerTable.get(table) ?? [])];
    const newGroup = [...newGroups[groupIndex]];
    newGroup[entryIndex] = { ...newGroup[entryIndex], account: newAccount };
    newGroups[groupIndex] = newGroup;
    newGroupsPerTable.set(table, newGroups);

    setRunParseResult({ ...runParseResult, groupsPerTable: newGroupsPerTable });
  };

  const [tabIndex, setTabIndex] = React.useState<number>(0);
  const currTable = TABLE_TYPES[tabIndex];

	return (
    <Stack>
      <Button onClick={() => exportAsExcel(runParseResult)}>
        Download
      </Button>
      <Divider />
      <SumView sumPerTable={runParseResult.sumPerTable} />
      <Divider />
      <Tabs value={tabIndex} onChange={(ev, newValue) => setTabIndex(newValue)}>
        { TABLE_TYPES.map((t) => <Tab label={t} key={t} />) }
      </Tabs>

      <TableResultView
        table={currTable}
        groups={runParseResult.groupsPerTable.get(currTable) ?? []}
        onAccountChange={(groupIndex, entryIndex, newAccount) => handleAccountChange(currTable, groupIndex, entryIndex, newAccount)}
      />
    </Stack>
	);
}

function SumView({
  sumPerTable,
}: {
  sumPerTable: Map<TableType, number>;
}) {
  let overallSum = 0;
  for (const t of TABLE_TYPES) {
    overallSum += sumPerTable.get(t) ?? 0;
  }

  return (
    <Box sx={{ p : 2 }}>
      {
        TABLE_TYPES.map((t) => (
          <div key={t}>
            {t}: {formatCurrency(sumPerTable.get(t) ?? 0)}
          </div>
        ))
      }
      <div>Overall: {formatCurrency(overallSum)}</div>
    </Box>
  );
}

function TableResultView({
  table,
  groups,
  onAccountChange,
}: {
  table: TableType;
  groups: Array<Array<JournalEntry>>;
  onAccountChange: (groupIndex: number, entryIndex: number, newAccount: Account) => void;
}) {
  return (
    <Stack>
      {
        groups.map((group, groupIndex) => {
          const balance = getBalance(group);

          return (
            <Accordion key={groupIndex}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <div>
                  <div>Journal Entry {groupIndex + 1}</div>
                  <div style={{ color: balance !== 0 ? 'red' : 'black' }}>Balance: {formatCurrency(balance)}</div>
                </div>
              </AccordionSummary>
              <AccordionDetails>
                <TableContainer component={Paper}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        {journalEntryHeaders.map(header => (
                          <TableCell key={header}>{header}</TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {group.map((entry, entryIndex) => (
                        <TableRow key={entryIndex}>
                          <TableCell>{formatDate(entry.date)}</TableCell>
                          <TableCell>{entry.description}</TableCell>
                          <EditableAccountCell
                            account={entry.account}
                            onAccountChange={(newAccount) => onAccountChange(groupIndex, entryIndex, newAccount)}
                          />
                          <TableCell>{entry.property}</TableCell>
                          <TableCell>{entry.tenant}</TableCell>
                          <TableCell>{entry.note}</TableCell>
                          <TableCell>{entry.creditAmount && formatCurrency(entry.creditAmount)}</TableCell>
                          <TableCell>{entry.debitAmount && formatCurrency(entry.debitAmount)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </AccordionDetails>
            </Accordion>
          );
        })
      }
    </Stack>
  );
}

function EditableAccountCell({
  account,
  onAccountChange,
}: {
  account: Account;
  onAccountChange: (newAccount: Account) => void;
}) {
  return (
    <TableCell sx={{ width: 300 }}>
      <Autocomplete
        options={ACCOUNTS}
        value={account}
        onChange={(event, newValue) => {
          if (newValue) {
            onAccountChange(newValue);
          }
        }}
        renderInput={(params) => <TextField {...params} />}
        disableClearable
      />
    </TableCell>
  );
}
